import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import { MousePointerClick } from 'lucide-react';
import { buildSopExtensions } from './tiptap/sopExtensions';
import { EMPTY_DOC } from './tiptap/sopJson';
import { flattenStepContent } from '../../utils/sopContent';
import SopEditorToolbar from './SopEditorToolbar';
import './sop-tiptap.css';

/**
 * SOP rich editor — a single free-form document (headings, paragraphs,
 * bullet/numbered lists, bold/italic). Full Google-Docs-style editing: delete a
 * section by selecting its text, reorder by cut/paste, Enter/Tab/Backspace all
 * work normally. Emits Tiptap JSON via onChange; the caller persists it into
 * `contentJSON`. Older step-format docs are flattened on the way in.
 */
const SopRichEditor = ({ value, onChange, mode = 'full', darkMode = false, toolbarSlot }) => {
  // When the caller passes `toolbarSlot` (even as null before the node mounts),
  // the formatting toolbar is portaled into that footer container instead of
  // rendered at the top of the editor.
  const useFooterSlot = toolbarSlot !== undefined;
  const editor = useEditor(
    {
      extensions: buildSopExtensions({ mode }),
      content: flattenStepContent(value || EMPTY_DOC),
      editable: true,
      onUpdate: ({ editor: ed }) => {
        onChange?.(ed.getJSON());
      },
    },
    [mode]
  );

  // Re-sync when the caller swaps in a different record; guard against loops by
  // skipping when the (flattened) incoming value already matches the editor.
  useEffect(() => {
    if (!editor) return;
    const incoming = flattenStepContent(value || EMPTY_DOC);
    if (JSON.stringify(editor.getJSON()) === JSON.stringify(incoming)) return;
    editor.commands.setContent(incoming, false);
  }, [value, editor]);

  return (
    <div className={`sop-editor${darkMode ? ' sop-dark' : ''}`}>
      {useFooterSlot
        ? (editor && toolbarSlot
            ? createPortal(
                <div className={`sop-footer-toolbar-wrap${darkMode ? ' sop-dark' : ''}`}>
                  <SopEditorToolbar editor={editor} />
                </div>,
                toolbarSlot
              )
            : null)
        : <SopEditorToolbar editor={editor} />}
      <div className="sop-edit-hint">
        <span className="sop-hint-pill">
          <MousePointerClick className="w-3 h-3" />
          Click to edit
        </span>
      </div>
      <EditorContent editor={editor} className="sop-editor-content" />
    </div>
  );
};

export default SopRichEditor;
